/**
 * Decision Impact View Component
 * Shows detailed decision impact per player with score deltas
 */

import React, { useState, useMemo } from 'react';
import { GameState, STAGES, StageId } from '../models/GameState';
import { PlayerState } from '../models/Player';
import { scoringSystem } from '../engine';
import { getPlayerIcon } from '@/config/playerIcons';
import { ScoreDeltaGroup } from './ScoreDeltaBadge';
import {
  getScoreDelta,
  getQuestionText,
  getAnswerText,
  isTextQuestion
} from '../utils/scoreDeltaCalculator';

interface DecisionImpactViewProps {
  gameState: GameState;
}

interface PlayerDecisionDisplay {
  questionId: string;
  answer: string;
  questionText: string;
  answerText: string;
  scoreDelta: { brain: number; heart: number; task: number; comm: number } | null;
  isTextAnswer: boolean;
  timestamp?: Date;
}

export const DecisionImpactView: React.FC<DecisionImpactViewProps> = ({ gameState }) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  // Get current stage info
  const currentStage = STAGES[gameState.currentStage as StageId];

  // Process player decisions into display format
  const playerDecisions = useMemo(() => {
    const result: Map<string, PlayerDecisionDisplay[]> = new Map();

    gameState.players.forEach(player => {
      const decisions: PlayerDecisionDisplay[] = [];

      // Get decisions from player.decisions Map
      player.decisions.forEach((answer, questionId) => {
        const isText = isTextQuestion(questionId);
        const answerStr = String(answer);

        decisions.push({
          questionId,
          answer: answerStr,
          questionText: getQuestionText(questionId),
          answerText: isText ? answerStr : getAnswerText(questionId, answerStr),
          scoreDelta: isText ? null : getScoreDelta(questionId, answerStr),
          isTextAnswer: isText
        });
      });

      // Sort by question ID (Q1, Q2, etc.)
      decisions.sort((a, b) => {
        const numA = parseInt(a.questionId.replace(/[^\d]/g, '')) || 0;
        const numB = parseInt(b.questionId.replace(/[^\d]/g, '')) || 0;
        return numA - numB;
      });

      result.set(player.playerId, decisions);
    });

    return result;
  }, [gameState.players]);

  // Get players to display (all or selected)
  const displayPlayers = selectedPlayerId
    ? gameState.players.filter(p => p.playerId === selectedPlayerId)
    : gameState.players;

  const renderPlayerCard = (player: PlayerState) => {
    const decisions = playerDecisions.get(player.playerId) || [];
    const totalScore = scoringSystem.calculateTotalScore(player.scores);
    const iconSrc = getPlayerIcon(player.role);

    return (
      <div
        key={player.playerId}
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
      >
        {/* Player Header */}
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {iconSrc && (
                <img
                  src={iconSrc}
                  alt={`${player.role} helmet`}
                  className="w-12 h-12 object-contain"
                />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-lg font-bold text-black">{player.name}</h4>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                    {player.role}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Brain: {player.scores.brain} | Heart: {player.scores.heart} | Task: {player.scores.task} | Comm: {player.scores.communication}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-black">{totalScore}</div>
              <div className="text-xs text-gray-500">Total Score</div>
            </div>
          </div>
        </div>

        {/* Decisions List */}
        <div className="divide-y divide-gray-100">
          {decisions.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 italic">
              No decisions recorded yet
            </div>
          ) : (
            decisions.map(decision => (
              <div key={decision.questionId} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Question */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {decision.questionId}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2 line-clamp-2">
                      {decision.questionText}
                    </p>

                    {/* Answer */}
                    <div className="mb-2">
                      <span className="text-xs text-gray-500">Answer: </span>
                      {decision.isTextAnswer ? (
                        <span className="text-sm font-medium text-black italic">
                          &quot;{decision.answerText}&quot;
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-black">
                          {decision.answer} - {decision.answerText}
                        </span>
                      )}
                    </div>

                    {/* Score Delta */}
                    {!decision.isTextAnswer && (
                      <ScoreDeltaGroup deltas={decision.scoreDelta} />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Current Stage Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-blue-600 font-medium">Current Stage</div>
            <div className="text-xl font-bold text-blue-800">
              Stage {gameState.currentStage}: {currentStage?.name || 'Unknown'}
            </div>
            {currentStage?.location && (
              <div className="text-sm text-blue-600">{currentStage.location}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-blue-600 font-medium">Step</div>
            <div className="text-lg font-semibold text-blue-800">{gameState.currentStep}</div>
          </div>
        </div>
      </div>

      {/* Player Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Filter:</span>
        <select
          value={selectedPlayerId || ''}
          onChange={(e) => setSelectedPlayerId(e.target.value || null)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Players</option>
          {gameState.players.map(player => (
            <option key={player.playerId} value={player.playerId}>
              {player.name} ({player.role})
            </option>
          ))}
        </select>
      </div>

      {/* Player Cards */}
      <div className="space-y-6">
        {displayPlayers.map(renderPlayerCard)}
      </div>
    </div>
  );
};

export default DecisionImpactView;
